const SHORT_BARCODE_MAX_LENGTH = 7;

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

const sanitizeShortBarcodeInput = (value, maxLength = SHORT_BARCODE_MAX_LENGTH) => {
  const digits = digitsOnly(value);
  return digits.slice(0, maxLength);
};

const normalizeShortBarcode = (value, maxLength = SHORT_BARCODE_MAX_LENGTH) => {
  const sanitized = sanitizeShortBarcodeInput(value, maxLength);
  return sanitized === '' ? null : sanitized;
};

const isEan13Format = (value) => /^\d{13}$/.test(value);

const getEan13CheckDigit = (digits12) => {
  if (!/^\d{12}$/.test(digits12)) return null;
  let sum = 0;
  for (let i = 0; i < digits12.length; i += 1) {
    const digit = Number(digits12[i]);
    sum += i % 2 === 0 ? digit : digit * 3;
  }
  return (10 - (sum % 10)) % 10;
};

const isValidEan13 = (value) => {
  if (!isEan13Format(value)) return false;
  const expected = getEan13CheckDigit(value.slice(0, 12));
  return expected !== null && expected === Number(value[12]);
};

const BARCODE_MODE_VALUES = ['weight', 'amount', 'price'];

const resolveBarcodeMode = (barcode, mappings) => {
  if (!isEan13Format(barcode)) return null;
  const list = Array.isArray(mappings) ? mappings : [];
  const normalized = list
    .map((item) => ({
      prefix: String(item?.prefix ?? '').trim(),
      mode: String(item?.mode ?? '').trim(),
    }))
    .filter((item) => item.prefix && BARCODE_MODE_VALUES.includes(item.mode));

  normalized.sort((a, b) => b.prefix.length - a.prefix.length);

  for (const mapping of normalized) {
    if (barcode.startsWith(mapping.prefix)) {
      return mapping.mode;
    }
  }

  return null;
};

const decodeVariableBarcode = (input, mode) => {
  const raw = String(input || '').trim();
  if (!isEan13Format(raw)) {
    return { isValid: false, reason: 'format' };
  }
  if (!BARCODE_MODE_VALUES.includes(mode)) {
    return { isValid: false, reason: 'mode' };
  }
  if (!isValidEan13(raw)) {
    return { isValid: false, isScale: true, reason: 'checksum' };
  }

  const pluRaw = raw.slice(1, 7);
  const encodedValueRaw = raw.slice(7, 12);
  const plu = parseInt(pluRaw, 10) || 0;
  const encodedValue = parseInt(encodedValueRaw, 10) || 0;

  const decoded = {
    isValid: true,
    isScale: true,
    mode,
    raw,
    prefix: raw[0],
    prefixPlu: raw.slice(0, 7),
    pluRaw,
    plu,
    encodedValue,
  };

  if (mode === 'weight') {
    decoded.weightKg = Number((encodedValue / 1000).toFixed(3));
  } else if (mode === 'amount') {
    decoded.amount = encodedValue;
  } else if (mode === 'price') {
    decoded.price = encodedValue;
  }

  return decoded;
};

const decodeScaleBarcode = (input) => {
  const raw = String(input || '').trim();
  if (!isEan13Format(raw)) {
    return { isValid: false, reason: 'format' };
  }
  if (raw[0] !== '2') {
    return { isValid: false, isScale: false, reason: 'non-scale' };
  }
  return decodeVariableBarcode(raw, 'weight');
};

const buildShortBarcodeLookupKeys = (input) => {
  const trimmed = String(input || '').trim();
  if (!/^\d+$/.test(trimmed)) return [];
  const sanitized = sanitizeShortBarcodeInput(trimmed);
  if (!sanitized) return [];
  const keys = new Set([sanitized]);
  const withoutZeros = sanitized.replace(/^0+/, '') || '0';
  keys.add(withoutZeros);
  return Array.from(keys);
};

const buildScaleBarcodeLookupKeys = (decoded) => {
  if (!decoded?.isValid) return [];
  const keys = new Set();
  if (decoded.prefixPlu) keys.add(decoded.prefixPlu);
  if (decoded.pluRaw) keys.add(decoded.pluRaw);
  if (typeof decoded.plu === 'number') keys.add(String(decoded.plu));
  return Array.from(keys);
};

export {
  SHORT_BARCODE_MAX_LENGTH,
  sanitizeShortBarcodeInput,
  normalizeShortBarcode,
  isValidEan13,
  resolveBarcodeMode,
  decodeVariableBarcode,
  decodeScaleBarcode,
  buildShortBarcodeLookupKeys,
  buildScaleBarcodeLookupKeys,
};
